import { isServer } from '@tanstack/react-query'
import { USER_STORE_KEY } from '@/store/useUserStore'

/**
 * 浏览器真实设备信息。
 *
 * 历史上 web 端把设备信息写死在 env（DEVICE-ID 全站共享一个值、品牌/机型伪装成
 * "xiaomi / M2011K2C"），后端日志与风控无法区分 web 设备。这里改为运行时按
 * 浏览器真实信息生成；SSR（无 navigator/localStorage）时回退 env 旧值，保持旧行为。
 *
 * 注意：PHONE-PLATFORM(web-client) 决定后端签名 salt 的选择，绝不能改为动态值。
 */

const DEVICE_ID_KEY = 'hw-device-id'

// 生成 32 位 hex（与 App 端 device id 格式一致）
const genDeviceId = (): string => {
  try {
    if (crypto.randomUUID) return crypto.randomUUID().replace(/-/g, '').toUpperCase()
    const buf = new Uint8Array(16)
    crypto.getRandomValues(buf)
    return Array.from(buf, b => b.toString(16).padStart(2, '0')).join('').toUpperCase()
  } catch {
    return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 18)}`.toUpperCase()
  }
}

/**
 * 设备唯一标识（同步读取）。
 *
 * id 由 ensureDeviceId()（应用启动时调用）异步生成并持久化到 localStorage：
 * 优先用 ThumbmarkJS 浏览器指纹——同一设备+浏览器可复现，清空 localStorage
 * 后重新计算仍得到同一 id，比随机 UUID 更适合风控与设备维度统计。
 *
 * 预热完成前（首屏最早的几个请求）以及 SSR/localStorage 不可用时，
 * 临时返回旧的共享值(env NEXT_PUBLIC_UA)且不写入，与历史行为一致；
 * 登录动作发生在用户交互之后，届时指纹必已就绪，登录态绑定的是稳定指纹 id。
 */
export const getDeviceId = (): string => {
  if (isServer) return process.env.NEXT_PUBLIC_UA!
  try {
    return localStorage.getItem(DEVICE_ID_KEY) || process.env.NEXT_PUBLIC_UA!
  } catch {
    // localStorage 不可用（隐私模式等）时退回共享值
    return process.env.NEXT_PUBLIC_UA!
  }
}

/**
 * 生成并持久化设备 id（应用启动时调用一次，幂等）。
 *
 * 优先级：
 * 1. localStorage 已有 → 不动（保持稳定，指纹漂移不影响既有设备）
 * 2. 存量已登录用户 → 沿用旧共享值。后端登录态(Redis)按 (userId, deviceId) 精确
 *    存取，老用户登录态绑定在共享 DEVICE-ID 上，换 id 会被判定登录过期。
 * 3. ThumbmarkJS 指纹（动态加载，不进首屏 bundle）→ 可复现 id
 * 4. 指纹失败（超时/被阻止等）→ 随机 UUID 兜底
 */
export const ensureDeviceId = async (): Promise<void> => {
  if (isServer) return
  try {
    if (localStorage.getItem(DEVICE_ID_KEY)) return

    const isLogined = () => {
      try {
        const store = localStorage.getItem(USER_STORE_KEY)
        return store ? !!JSON.parse(store)?.state?.isLogined : false
      } catch {
        return false
      }
    }
    if (isLogined()) {
      localStorage.setItem(DEVICE_ID_KEY, process.env.NEXT_PUBLIC_UA!)
      return
    }

    let id: string
    try {
      const { getFingerprint } = await import('@thumbmarkjs/thumbmarkjs')
      id = (await getFingerprint()).toUpperCase()
    } catch {
      id = genDeviceId()
    }
    // 指纹计算期间用户已登录：登录态绑定的是当时返回的共享值，沿用它
    if (isLogined()) id = process.env.NEXT_PUBLIC_UA!
    // 双检：指纹计算期间可能已被其他 tab 写入（结果幂等，以先写为准）
    if (!localStorage.getItem(DEVICE_ID_KEY)) {
      localStorage.setItem(DEVICE_ID_KEY, id)
    }
  } catch { }
}

/** 浏览器品牌（对应 App 端的手机品牌 PHONE-BRAND） */
export const getPhoneBrand = (): string => {
  if (isServer) return process.env.NEXT_PUBLIC_PHONE_BRAND!
  const ua = navigator.userAgent
  if (/edg(a|ios)?\//i.test(ua)) return 'edge'
  if (/opr\/|opera/i.test(ua)) return 'opera'
  if (/samsungbrowser/i.test(ua)) return 'samsung-browser'
  if (/chrome|crios/i.test(ua)) return 'chrome'
  if (/firefox|fxios/i.test(ua)) return 'firefox'
  if (/safari/i.test(ua)) return 'safari'
  return 'browser'
}

/** 操作系统（对应 App 端的手机机型 PHONE-MODEL） */
export const getPhoneModel = (): string => {
  if (isServer) return process.env.NEXT_PUBLIC_OLD_PHONE_MODEL!
  const ua = navigator.userAgent
  if (/iphone|ipod/i.test(ua)) return 'ios'
  if (/ipad/i.test(ua)) return 'ipados'
  if (/android/i.test(ua)) return 'android'
  if (/windows/i.test(ua)) return 'windows'
  // iPadOS 13+ 的 Safari 默认桌面 UA 伪装成 Mac，用触点数区分
  if (/mac os x|macintosh/i.test(ua)) return navigator.maxTouchPoints > 1 ? 'ipados' : 'macos'
  if (/linux/i.test(ua)) return 'linux'
  return 'web'
}
